/**
 * When a plant does what, *here*.
 *
 * ## The one calculation
 *
 * The catalog's phenology days — leaf-out 100, autumn colour 280, leaf drop 315 — were measured in a
 * particular climate, and this module moves them to a different one. It does it with a single idea:
 * **the growing season's two ends move independently, and each plant follows them by its own
 * fraction.**
 *
 * A site whose last frost is three weeks later than the reference climate's has a spring that starts
 * three weeks late. A serviceberry, which blooms on accumulated warmth, follows nearly all of that. A
 * plant triggered by day length follows almost none of it. `springResponsiveness` on the record is
 * that fraction, and the autumn end has its own, because a long warm autumn delays leaf drop far more
 * than it delays anything in spring.
 *
 * ## Why it produces a whole `FloraSeasonDefinition`
 *
 * {@link ResolvedPhenology} carries a *localized copy* of the record's `seasons` block, with the days
 * moved and everything else untouched. That copy is what the renderer consumes, through the very same
 * `resolveSeasonalAppearance` it always used. Two things follow, and both matter:
 *
 * - When the shifts are zero — an unlocalized site, resolving against its own reference climate — the
 *   copy is byte-for-byte the original, so the app draws exactly what it drew before this feature
 *   existed. Localization is additive by construction rather than by careful testing.
 * - The renderer contains no geography. It is handed a season definition, as it always was; it never
 *   learns what a hardiness zone is. See `FloraResolver.getPlantState`.
 *
 * ## Room above it
 *
 * The shift is linear in frost date, which is a first approximation of a thermal model. A real
 * Growing Degree Day accumulation would replace {@link shiftForDay} and nothing else: the inputs it
 * needs are already optional fields on `SiteLocalization`, and every consumer downstream reads dates
 * out of this structure rather than computing its own.
 */

import type {
  BloomWindow,
  FloraDefinition,
  FloraSeasonDefinition,
} from '@/flora/FloraDefinition';
import { DAYS_IN_YEAR, wrapDay } from '@/environment/SeasonModel';
import {
  REFERENCE_ZONE,
  estimateFrostWindow,
  zoneFromValue,
  zoneValue,
  type FrostWindow,
  type SiteLocalization,
} from './SiteLocalization';

/**
 * How much of the site's spring shift a plant follows when its record does not say.
 *
 * 0.8 rather than 1.0 because almost nothing is purely thermal — even the most warmth-driven woody
 * plants carry some photoperiod component, and a default of 1.0 would move a magnolia a full month
 * between Georgia and Vermont, which overstates what actually happens.
 */
const DEFAULT_SPRING_RESPONSIVENESS = 0.8;

/**
 * The autumn default is lower.
 *
 * Leaf drop and autumn colour are triggered by shortening days at least as much as by cold, which is
 * why a warm October delays colour by days rather than by the weeks its frost date moved.
 */
const DEFAULT_AUTUMN_RESPONSIVENESS = 0.55;

/** A span of the year. `endDay` may be less than `startDay`, which means the window wraps. */
export interface DayWindow {
  startDay: number;
  endDay: number;
}

export interface ResolvedPhenology {
  /** Days the spring events moved. Negative is earlier than the reference climate. */
  springShiftDays: number;
  /** Days the autumn events moved. Positive is later. */
  autumnShiftDays: number;
  leafOutDay?: number;
  autumnColorStartDay?: number;
  leafDropDay?: number;
  /** When growth has fully stopped — leaf drop for most, an explicit day for evergreens and grasses. */
  dormancyDay?: number;
  /** The record's bloom windows, moved. Empty when the record has none. */
  bloomWindows: BloomWindow[];
  /** Ripe fruit on the plant, from maturity to the end of persistence. */
  fruitWindow?: DayWindow;
  /** Seed available on the plant. */
  seedWindow?: DayWindow;
  /** When there are leaves at all. The whole year for an evergreen. */
  foliageWindow: DayWindow;
  /**
   * The record's `seasons` block with every day moved — what the renderer is handed.
   *
   * Identical to the original when both shifts are zero. See the note at the top of this file.
   */
  seasons: FloraSeasonDefinition;
}

/**
 * A deliberate adjustment to a plant's whole calendar, on top of what the site resolved.
 *
 * Not to be confused with `FloraInstance.seasonalOffsetDays`, which is render-time variation applied
 * when the appearance is evaluated rather than a claim about when the plant does things — see
 * `FloraResolver.getPlantState` for why the two must stay apart. Nothing in the app writes this
 * today; it is the hook for "this specimen is a fortnight behind the rest" when there is a UI to say
 * so with.
 */
export interface PhenologyOverrides {
  phenologyOffsetDays?: number;
}

/** The frost window the record's baseline days were authored against. */
export function referenceFrostFor(definition: FloraDefinition): FrostWindow {
  const zone = definition.phenology?.referenceZone;
  return estimateFrostWindow(
    zone === undefined ? REFERENCE_ZONE : zoneFromValue(zone),
  );
}

/**
 * How far an event on `day` moves, blending between the spring and autumn shifts.
 *
 * Continuous rather than a two-way switch, because the year is continuous: a June bloom is genuinely
 * governed by neither frost date alone, and stepping the shift at some arbitrary midpoint would make
 * two plants blooming a week apart move by different amounts for no botanical reason.
 *
 * Anchored on the *reference* frost dates, since `day` is a reference-climate day — the thing being
 * asked is where in that climate's year the event sat.
 */
function shiftForDay(
  day: number,
  reference: FrostWindow,
  springShift: number,
  autumnShift: number,
): number {
  const spring = reference.lastSpringFrostDay;
  const autumn = reference.firstAutumnFrostDay;
  if (day <= spring) return springShift;
  if (day >= autumn || autumn <= spring) return autumnShift;
  const t = (day - spring) / (autumn - spring);
  return springShift + (autumnShift - springShift) * t;
}

/**
 * Resolve a plant's calendar for a site.
 *
 * Pure and deterministic: same record, same profile, same overrides, same answer — which is what
 * makes the resolver's cache safe and the seasonal slider reproducible.
 */
export function resolvePhenology(
  definition: FloraDefinition,
  localization: SiteLocalization,
  overrides: PhenologyOverrides = {},
): ResolvedPhenology {
  const reference = referenceFrostFor(definition);
  const phenology = definition.phenology;

  const springResponse = clamp01(phenology?.springResponsiveness ?? DEFAULT_SPRING_RESPONSIVENESS);
  const autumnResponse = clamp01(phenology?.autumnResponsiveness ?? DEFAULT_AUTUMN_RESPONSIVENESS);
  const manual = Math.trunc(overrides.phenologyOffsetDays ?? 0);

  const springShiftDays = Math.round(
    (localization.frost.lastSpringFrostDay - reference.lastSpringFrostDay) * springResponse,
  );
  const autumnShiftDays = Math.round(
    (localization.frost.firstAutumnFrostDay - reference.firstAutumnFrostDay) * autumnResponse,
  );

  /** Move one reference-climate day to this site, including the specimen's own adjustment. */
  const move = (day: number): number =>
    wrapDay(day + Math.round(shiftForDay(day, reference, springShiftDays, autumnShiftDays)) + manual);

  const seasons = definition.seasons;
  const leafOutDay = seasons.leafOutDay === undefined ? undefined : move(seasons.leafOutDay);
  const autumnColorStartDay =
    seasons.autumnColorStartDay === undefined ? undefined : move(seasons.autumnColorStartDay);
  const leafDropDay = seasons.leafDropDay === undefined ? undefined : move(seasons.leafDropDay);

  const bloomWindows = (seasons.bloomWindows ?? []).map((window) => ({
    ...window,
    startDayOfYear: move(window.startDayOfYear),
    endDayOfYear: move(window.endDayOfYear),
  }));

  const localizedSeasons: FloraSeasonDefinition = {
    ...seasons,
    ...(leafOutDay === undefined ? {} : { leafOutDay }),
    ...(autumnColorStartDay === undefined ? {} : { autumnColorStartDay }),
    ...(leafDropDay === undefined ? {} : { leafDropDay }),
    ...(seasons.bloomWindows === undefined ? {} : { bloomWindows }),
  };

  const resolved: ResolvedPhenology = {
    springShiftDays,
    autumnShiftDays,
    ...(leafOutDay === undefined ? {} : { leafOutDay }),
    ...(autumnColorStartDay === undefined ? {} : { autumnColorStartDay }),
    ...(leafDropDay === undefined ? {} : { leafDropDay }),
    bloomWindows,
    foliageWindow: foliageWindowOf(seasons, leafOutDay, leafDropDay),
    seasons: localizedSeasons,
  };

  const dormancyDay = phenology?.dormancyDay;
  if (dormancyDay !== undefined) resolved.dormancyDay = move(dormancyDay);
  else if (leafDropDay !== undefined) resolved.dormancyDay = leafDropDay;

  const fruit = fruitWindowOf(definition, bloomWindows, leafOutDay);
  if (fruit) resolved.fruitWindow = fruit;

  const seed = seedWindowOf(definition, bloomWindows, leafOutDay);
  if (seed) resolved.seedWindow = seed;

  return resolved;
}

/** Leaf-out to leaf drop, or the whole year for anything that keeps its leaves. */
function foliageWindowOf(
  seasons: FloraSeasonDefinition,
  leafOutDay: number | undefined,
  leafDropDay: number | undefined,
): DayWindow {
  if (seasons.evergreen) return { startDay: 1, endDay: DAYS_IN_YEAR };
  return { startDay: leafOutDay ?? 100, endDay: leafDropDay ?? 300 };
}

/**
 * When ripe fruit is on the plant.
 *
 * Anchored to the *end of bloom* rather than to a stored date, because that is the botanical
 * relationship: fruit follows the flower it came from, so a bloom that moved three weeks late in
 * Vermont carries its fruit three weeks late too. This is the mechanism the spec's worked example
 * describes — "fruit persists roughly 90 days after maturation" plus a localized bloom gives a
 * localized bird-food window, with no per-site calendar stored anywhere.
 *
 * Produced only where the record supports it: an explicit maturation figure, or a season block that
 * actually shows fruit. A plant with neither gets no window rather than an invented one.
 */
function fruitWindowOf(
  definition: FloraDefinition,
  bloomWindows: readonly BloomWindow[],
  leafOutDay: number | undefined,
): DayWindow | undefined {
  const phenology = definition.phenology;
  const showsFruit = (['spring', 'summer', 'autumn', 'winter'] as const).some(
    (season) => (definition.seasons[season].fruitDensity ?? 0) > 0,
  );
  if (phenology?.fruitMaturationDays === undefined && !showsFruit) return undefined;

  const maturation = phenology?.fruitMaturationDays ?? 90;
  const persistence = phenology?.fruitPersistenceDays ?? 60;
  const anchor = bloomEnd(bloomWindows) ?? wrapDay((leafOutDay ?? 100) + 40);
  const startDay = wrapDay(anchor + maturation);
  return { startDay, endDay: wrapDay(startDay + persistence) };
}

/** The same, for seed. Falls back to the fruit timings, since for many plants they are one event. */
function seedWindowOf(
  definition: FloraDefinition,
  bloomWindows: readonly BloomWindow[],
  leafOutDay: number | undefined,
): DayWindow | undefined {
  const phenology = definition.phenology;
  if (phenology?.seedMaturationDays === undefined && phenology?.seedPersistenceDays === undefined) {
    return undefined;
  }
  const maturation = phenology.seedMaturationDays ?? phenology.fruitMaturationDays ?? 80;
  const persistence = phenology.seedPersistenceDays ?? 90;
  const anchor = bloomEnd(bloomWindows) ?? wrapDay((leafOutDay ?? 100) + 40);
  const startDay = wrapDay(anchor + maturation);
  return { startDay, endDay: wrapDay(startDay + persistence) };
}

/** End of the last bloom window in the year, or undefined when the plant does not bloom. */
function bloomEnd(windows: readonly BloomWindow[]): number | undefined {
  if (windows.length === 0) return undefined;
  let latest = windows[0] as BloomWindow;
  for (const window of windows) {
    if (window.endDayOfYear > latest.endDayOfYear) latest = window;
  }
  return latest.endDayOfYear;
}

/* --------------------------------- Utilities --------------------------------- */

/** Whether a day falls inside a window, handling windows that wrap the year end. */
export function windowContains(window: DayWindow, dayOfYear: number): boolean {
  const day = wrapDay(dayOfYear);
  if (window.startDay <= window.endDay) return day >= window.startDay && day <= window.endDay;
  return day >= window.startDay || day <= window.endDay;
}

/** Length of a window in days, counting a wrapped window the long way round the year. */
export function windowLength(window: DayWindow): number {
  const span = window.endDay - window.startDay;
  return span >= 0 ? span + 1 : span + DAYS_IN_YEAR + 1;
}

/**
 * Whether this site's growing season is long enough for the plant to finish what it starts.
 *
 * Not a hardiness question. A fig survives a mild winter and still never ripens fruit in a 120-day
 * season, and that is worth saying separately from "it will freeze".
 */
export function seasonFitsPlant(
  definition: FloraDefinition,
  localization: SiteLocalization,
): { fits: boolean; requiredDays: number; availableDays: number } | undefined {
  const required = definition.climate?.minimumGrowingSeasonDays;
  if (required === undefined) return undefined;
  const available =
    localization.frost.firstAutumnFrostDay - localization.frost.lastSpringFrostDay;
  return { fits: available >= required, requiredDays: required, availableDays: Math.max(0, available) };
}

/** The zone value a record's baseline calendar was authored for. */
export function referenceZoneValue(definition: FloraDefinition): number {
  return definition.phenology?.referenceZone ?? zoneValue(REFERENCE_ZONE);
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}
